import { useClientSelection } from "../client-context";
import { clientOptions } from "../client-options";
import Dialog from "./Dialog";
import { ActionForm, useMutation, type InputField } from "./Workflow";

/** routes/projects.js: the types with a phase template get their standard phases. */
export const PROJECT_TYPES = [
  "campaign_launch",
  "campaign_rebuild",
  "full_service_onboarding",
  "seo_project",
  "website_update_franchise",
  "analytics_tracking_setup",
  "optimization_sprint",
  "corporate_change_request",
  "internal",
  "other",
];

// The choices routes/projects.js accepts, shared by every project form (agency
// Projects, a client's Projects list and its Phases tab). A page with its own
// shorter list opened a Critical project on Medium and a Skipped phase on
// Pending, and saving wrote that back.
/** Project status, in the order work moves through it. */
export const PROJECT_STATUSES = ["planning", "active", "blocked", "on_hold", "completed", "cancelled"];
/** Most urgent first. A project saved with no priority counts as medium. */
export const PROJECT_PRIORITIES = ["critical", "high", "medium", "low"];
/** What a phase can be set to. The API also has waiting_external, kept as a choice on a phase that already has it. */
export const PHASE_STATUSES = ["pending", "active", "blocked", "completed", "skipped"];
/** The phase choices plus the phase's own status when it is another one, so opening and saving never changes it. */
export const phaseStatusOptions = (current: unknown) =>
  typeof current === "string" && current && !PHASE_STATUSES.includes(current) ? [...PHASE_STATUSES, current] : PHASE_STATUSES;

/**
 * New project, in the app's shared form layout (labels above full-width fields,
 * Cancel beside Create). On a client's Projects page the client is fixed
 * (`clientId`); on the agency Projects page it is a choice: "Agency (no client)"
 * for internal work, then active clients, then paused ones marked "(paused)".
 */
export default function NewProjectDialog({
  clientId,
  clientName,
  onClose,
  onCreated,
}: {
  clientId?: number | null;
  clientName?: string;
  onClose: () => void;
  onCreated: (message: string) => void;
}) {
  const { clients } = useClientSelection();
  const fixed = clientId != null;
  const mutation = useMutation();
  const fields: InputField[] = [
    { name: "name", label: "Project name", required: true },
    { name: "project_type", label: "Type", options: PROJECT_TYPES, value: PROJECT_TYPES[0] },
    ...(fixed
      ? []
      : [{ name: "client_id", label: "Client", options: [{ value: "", label: "Agency (no client)" }, ...clientOptions(clients.data).map(({ value, label }) => ({ value, label }))], value: "" }]),
    { name: "due_date", label: "Due date", type: "date" },
    { name: "description", label: "Description", type: "textarea" },
  ];
  return (
    <Dialog title="New project" description={fixed && clientName ? `For ${clientName}` : undefined} onClose={() => { if (!mutation.busy) onClose(); }}>
      <ActionForm
        fields={fields}
        submit="Create project"
        busy={mutation.busy}
        busyLabel="Creating…"
        onCancel={onClose}
        onSubmit={async (data) => {
          const name = String(data.name || "").trim();
          const result = await mutation.run("/api/projects", {
            name,
            project_type: data.project_type,
            client_id: fixed ? clientId : data.client_id ? Number(data.client_id) : null,
            due_date: data.due_date || null,
            description: String(data.description || "").trim() || null,
            use_phase_template: true,
          });
          if (result) onCreated(`${name} created.`);
        }}
      />
      {mutation.feedback}
    </Dialog>
  );
}
